import { useEffect, useState } from 'react'
import useSettingsStore from '../store/settingsStore'
import useBitsStore from '../store/bitsStore'
import '../styles/Plant.css'
import '../styles/BitsVariants.css'

// Plant visual configurations
const PLANT_CONFIGS = {
  flower: {
    stemColor: '#2d5a27',
    petalColors: ['#ff6b9d', '#ff8fab', '#ffb3c6'],
    centerColor: '#ffd93d',
    leafColor: '#3d7a33'
  },
  mushroom: {
    capColor: '#e74c3c',
    spotColor: '#fff',
    stemColor: '#f5e6d3',
    gillColor: '#d4a574'
  },
  cactus: {
    bodyColor: '#27ae60',
    spineColor: '#f1c40f',
    flowerColor: '#e91e63'
  },
  sunflower: {
    stemColor: '#2d5a27',
    petalColor: '#f1c40f',
    centerColor: '#8b4513',
    leafColor: '#3d7a33'
  },
  tulip: {
    stemColor: '#2d5a27',
    petalColors: ['#9b59b6', '#8e44ad', '#6c3483'],
    leafColor: '#27ae60'
  },
  rose: {
    stemColor: '#2d5a27',
    petalColors: ['#c0392b', '#e74c3c', '#ff6b6b'],
    leafColor: '#27ae60',
    thornColor: '#1e3d14'
  },
  tree: {
    trunkColor: '#8b4513',
    leafColors: ['#27ae60', '#2ecc71', '#58d68d'],
    fruitColor: '#e74c3c'
  },
  fern: {
    frondColor: '#27ae60',
    stemColor: '#1e8449'
  },
  succulent: {
    leafColors: ['#76d7c4', '#48c9b0', '#1abc9c'],
    potColor: '#935116'
  },
  bamboo: {
    stalkColor: '#7dcea0',
    nodeColor: '#27ae60',
    leafColor: '#2ecc71'
  },
  vine: {
    vineColor: '#27ae60',
    leafColor: '#2ecc71',
    flowerColor: '#af7ac5'
  },
  bonsai: {
    trunkColor: '#6e4b3a',
    leafColor: '#27ae60',
    potColor: '#5d4e37'
  },
  // Legendary plants (500 bits)
  cherry_blossom: {
    trunkColor: '#8b5e3c',
    branchColor: '#6e4b3a',
    petalColors: ['#ffb7c5', '#ff91a4', '#ff69b4'],
    leafColor: '#a8d8a8'
  },
  crystal_tree: {
    trunkColor: '#7b8fa8',
    crystalColors: ['#a8d4ff', '#c8b4ff', '#b4ffed'],
    baseColor: '#4a5568'
  },
  venus_flytrap: {
    stemColor: '#2d5a27',
    trapColor: '#27ae60',
    innerColor: '#e74c3c',
    teethColor: '#d4edda',
    leafColor: '#3d7a33'
  }
}

export function TerrariumPlant({ plant, compact = false }) {
  const settings = useSettingsStore((state) => state.settings)
  const [harvestSparkles, setHarvestSparkles] = useState([])

  const {
    userId,
    username,
    plantType,
    growthStage,
    health,
    isWatering,
    isHarvesting,
    customName,
    variant = 'default'
  } = plant

  const config = PLANT_CONFIGS[plantType] || PLANT_CONFIGS.flower

  // Calculate visual properties based on growth stage
  // Compact mode: for wide terrarium with multiple plants
  const baseScale = compact
    ? 0.5 + (growthStage / 100) * 0.7
    : 0.4 + (growthStage / 100) * 0.8
  const scale = baseScale * (settings.plantScale || (compact ? 1.2 : 1.5))
  const opacity = Math.max(0.5, health / 100)
  const isWilting = health < 30
  const isMature = growthStage >= 100

  // Generate harvest sparkles
  useEffect(() => {
    if (isHarvesting) {
      const sparkles = Array.from({ length: 12 }, (_, i) => ({
        id: i,
        left: 20 + Math.random() * 60,
        top: 20 + Math.random() * 60,
        delay: Math.random() * 0.3
      }))
      setHarvestSparkles(sparkles)
    } else {
      setHarvestSparkles([])
    }
  }, [isHarvesting])

  // Render different plant types
  const renderPlant = () => {
    switch (plantType) {
      case 'mushroom':
        return <MushroomPlant config={config} growthStage={growthStage} />
      case 'cactus':
        return <CactusPlant config={config} growthStage={growthStage} />
      case 'sunflower':
        return <SunflowerPlant config={config} growthStage={growthStage} />
      case 'tulip':
        return <TulipPlant config={config} growthStage={growthStage} />
      case 'rose':
        return <RosePlant config={config} growthStage={growthStage} />
      case 'tree':
        return <TreePlant config={config} growthStage={growthStage} />
      case 'fern':
        return <FernPlant config={config} growthStage={growthStage} />
      case 'succulent':
        return <SucculentPlant config={config} growthStage={growthStage} />
      case 'bamboo':
        return <BambooPlant config={config} growthStage={growthStage} />
      case 'vine':
        return <VinePlant config={config} growthStage={growthStage} />
      case 'bonsai':
        return <BonsaiPlant config={config} growthStage={growthStage} />
      case 'cherry_blossom':
        return <CherryBlossomPlant config={config} growthStage={growthStage} />
      case 'crystal_tree':
        return <CrystalTreePlant config={config} growthStage={growthStage} />
      case 'venus_flytrap':
        return <VenusFlyTrapPlant config={config} growthStage={growthStage} />
      default:
        return <FlowerPlant config={config} growthStage={growthStage} />
    }
  }

  return (
    <div
      className={`plant ${isWilting ? 'wilting' : ''} ${isMature ? 'mature' : ''} ${variant !== 'default' ? `variant-${variant}` : ''}`}
      style={{
        transform: `scale(${scale})`,
        opacity,
        position: 'relative'
      }}
    >
      <div className={`plant-body ${isWatering ? 'watering' : ''} ${isHarvesting ? 'harvesting' : ''}`}>
        {renderPlant()}
      </div>

      {/* Harvest sparkles */}
      {harvestSparkles.map(sparkle => (
        <div
          key={sparkle.id}
          className="harvest-sparkle"
          style={{
            left: `${sparkle.left}%`,
            top: `${sparkle.top}%`,
            animationDelay: `${sparkle.delay}s`
          }}
        />
      ))}

    </div>
  )
}

// Individual plant type components (same as before but simplified)
function FlowerPlant({ config, growthStage }) {
  const showPetals = growthStage >= 60
  const showBud = growthStage >= 30 && growthStage < 60

  return (
    <div className="flower-plant">
      <div className="stem" style={{ backgroundColor: config.stemColor }}>
        {growthStage >= 40 && (
          <>
            <div className="leaf left" style={{ backgroundColor: config.leafColor }} />
            <div className="leaf right" style={{ backgroundColor: config.leafColor }} />
          </>
        )}
      </div>
      <div className="flower-head">
        {showBud && (
          <div className="bud" style={{ backgroundColor: config.petalColors[0] }} />
        )}
        {showPetals && (
          <>
            {config.petalColors.map((color, i) => (
              <div
                key={i}
                className={`petal petal-${i + 1}`}
                style={{
                  backgroundColor: color,
                  transform: `rotate(${i * 45}deg) translateY(-15px)`
                }}
              />
            ))}
            <div className="flower-center" style={{ backgroundColor: config.centerColor }} />
          </>
        )}
      </div>
    </div>
  )
}

function MushroomPlant({ config, growthStage }) {
  const showCap = growthStage >= 40
  const showSpots = growthStage >= 70

  return (
    <div className="mushroom-plant">
      {showCap && (
        <div className="mushroom-cap" style={{ backgroundColor: config.capColor }}>
          {showSpots && (
            <>
              <div className="mushroom-spot spot-1" style={{ backgroundColor: config.spotColor }} />
              <div className="mushroom-spot spot-2" style={{ backgroundColor: config.spotColor }} />
              <div className="mushroom-spot spot-3" style={{ backgroundColor: config.spotColor }} />
            </>
          )}
        </div>
      )}
      <div className="mushroom-stem" style={{ backgroundColor: config.stemColor }}>
        {growthStage >= 50 && (
          <div className="mushroom-gills" style={{ backgroundColor: config.gillColor }} />
        )}
      </div>
    </div>
  )
}

function CactusPlant({ config, growthStage }) {
  const showArms = growthStage >= 50
  const showFlower = growthStage >= 90

  return (
    <div className="cactus-plant">
      <div className="cactus-body" style={{ backgroundColor: config.bodyColor }}>
        <div className="cactus-spines" style={{ borderColor: config.spineColor }} />
      </div>
      {showArms && (
        <>
          <div className="cactus-arm left" style={{ backgroundColor: config.bodyColor }} />
          <div className="cactus-arm right" style={{ backgroundColor: config.bodyColor }} />
        </>
      )}
      {showFlower && (
        <div className="cactus-flower" style={{ backgroundColor: config.flowerColor }} />
      )}
    </div>
  )
}

function SunflowerPlant({ config, growthStage }) {
  const showHead = growthStage >= 50
  const petalCount = Math.min(12, Math.floor(growthStage / 8))

  return (
    <div className="sunflower-plant">
      <div className="sunflower-stem" style={{ backgroundColor: config.stemColor }}>
        {growthStage >= 30 && (
          <>
            <div className="sunflower-leaf left" style={{ backgroundColor: config.leafColor }} />
            <div className="sunflower-leaf right" style={{ backgroundColor: config.leafColor }} />
          </>
        )}
      </div>
      {showHead && (
        <div className="sunflower-head">
          {Array.from({ length: petalCount }).map((_, i) => (
            <div
              key={i}
              className="sunflower-petal"
              style={{
                backgroundColor: config.petalColor,
                transform: `rotate(${i * (360 / petalCount)}deg) translateY(-20px)`
              }}
            />
          ))}
          <div className="sunflower-center" style={{ backgroundColor: config.centerColor }} />
        </div>
      )}
    </div>
  )
}

function TulipPlant({ config, growthStage }) {
  const showBulb = growthStage >= 50
  const bloomLevel = Math.max(0, growthStage - 50) / 50

  return (
    <div className="tulip-plant">
      <div className="tulip-stem" style={{ backgroundColor: config.stemColor }} />
      {growthStage >= 20 && (
        <>
          <div className="tulip-leaf left" style={{ backgroundColor: config.leafColor }} />
          <div className="tulip-leaf right" style={{ backgroundColor: config.leafColor }} />
        </>
      )}
      {showBulb && (
        <div className="tulip-bulb" style={{ transform: `translateX(-50%) scaleX(${0.5 + bloomLevel * 0.5})` }}>
          {config.petalColors.map((color, i) => (
            <div
              key={i}
              className={`tulip-petal petal-${i + 1}`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function RosePlant({ config, growthStage }) {
  const showBud = growthStage >= 40
  const petalLayers = Math.min(3, Math.floor((growthStage - 40) / 20))

  return (
    <div className="rose-plant">
      <div className="rose-stem" style={{ backgroundColor: config.stemColor }}>
        <div className="rose-thorn thorn-1" style={{ backgroundColor: config.thornColor }} />
        <div className="rose-thorn thorn-2" style={{ backgroundColor: config.thornColor }} />
      </div>
      {growthStage >= 25 && (
        <div className="rose-leaves" style={{ backgroundColor: config.leafColor }} />
      )}
      {showBud && (
        <div className="rose-bloom">
          {Array.from({ length: petalLayers }).map((_, layer) => (
            <div key={layer} className={`rose-layer layer-${layer + 1}`}>
              {config.petalColors.slice(0, layer + 2).map((color, i) => (
                <div
                  key={i}
                  className="rose-petal"
                  style={{
                    backgroundColor: color,
                    transform: `rotate(${i * (180 / (layer + 2))}deg)`
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function TreePlant({ config, growthStage }) {
  const showFoliage = growthStage >= 30
  const showFruit = growthStage >= 90

  return (
    <div className="tree-plant">
      <div className="tree-trunk" style={{ backgroundColor: config.trunkColor }} />
      {showFoliage && (
        <div className="tree-foliage">
          {config.leafColors.map((color, i) => (
            <div
              key={i}
              className={`foliage-layer layer-${i + 1}`}
              style={{
                backgroundColor: color,
                opacity: growthStage >= 30 + i * 20 ? 1 : 0
              }}
            />
          ))}
        </div>
      )}
      {showFruit && (
        <>
          <div className="tree-fruit fruit-1" style={{ backgroundColor: config.fruitColor }} />
          <div className="tree-fruit fruit-2" style={{ backgroundColor: config.fruitColor }} />
        </>
      )}
    </div>
  )
}

function FernPlant({ config, growthStage }) {
  const frondCount = Math.min(5, Math.floor(growthStage / 20))

  return (
    <div className="fern-plant">
      <div className="fern-base" style={{ backgroundColor: config.stemColor }} />
      {Array.from({ length: frondCount }).map((_, i) => (
        <div
          key={i}
          className={`fern-frond frond-${i + 1}`}
          style={{
            backgroundColor: config.frondColor,
            transform: `rotate(${-60 + i * 30}deg)`
          }}
        />
      ))}
    </div>
  )
}

function SucculentPlant({ config, growthStage }) {
  const leafCount = Math.min(8, Math.floor(growthStage / 12))

  return (
    <div className="succulent-plant">
      <div className="succulent-pot" style={{ backgroundColor: config.potColor }} />
      <div className="succulent-rosette">
        {Array.from({ length: leafCount }).map((_, i) => (
          <div
            key={i}
            className="succulent-leaf"
            style={{
              backgroundColor: config.leafColors[i % config.leafColors.length],
              transform: `rotate(${i * (360 / leafCount)}deg) translateY(-8px)`
            }}
          />
        ))}
      </div>
    </div>
  )
}

function BambooPlant({ config, growthStage }) {
  const segments = Math.min(5, Math.floor(growthStage / 20))
  const showLeaves = growthStage >= 60

  return (
    <div className="bamboo-plant">
      {Array.from({ length: segments }).map((_, i) => (
        <div key={i} className="bamboo-segment">
          <div className="bamboo-stalk" style={{ backgroundColor: config.stalkColor }} />
          <div className="bamboo-node" style={{ backgroundColor: config.nodeColor }} />
        </div>
      ))}
      {showLeaves && (
        <div className="bamboo-leaves">
          <div className="bamboo-leaf leaf-1" style={{ backgroundColor: config.leafColor }} />
          <div className="bamboo-leaf leaf-2" style={{ backgroundColor: config.leafColor }} />
          <div className="bamboo-leaf leaf-3" style={{ backgroundColor: config.leafColor }} />
        </div>
      )}
    </div>
  )
}

function VinePlant({ config, growthStage }) {
  const segments = Math.min(4, Math.floor(growthStage / 25))
  const showFlowers = growthStage >= 80

  return (
    <div className="vine-plant">
      {Array.from({ length: segments }).map((_, i) => (
        <div
          key={i}
          className={`vine-segment segment-${i + 1}`}
          style={{ backgroundColor: config.vineColor }}
        >
          <div className="vine-leaf" style={{ backgroundColor: config.leafColor }} />
        </div>
      ))}
      {showFlowers && (
        <>
          <div className="vine-flower flower-1" style={{ backgroundColor: config.flowerColor }} />
          <div className="vine-flower flower-2" style={{ backgroundColor: config.flowerColor }} />
        </>
      )}
    </div>
  )
}

function BonsaiPlant({ config, growthStage }) {
  const showCanopy = growthStage >= 40

  return (
    <div className="bonsai-plant">
      <div className="bonsai-pot" style={{ backgroundColor: config.potColor }} />
      <div className="bonsai-trunk" style={{ backgroundColor: config.trunkColor }}>
        {growthStage >= 30 && (
          <div className="bonsai-branch" style={{ backgroundColor: config.trunkColor }} />
        )}
      </div>
      {showCanopy && (
        <div className="bonsai-canopy">
          <div className="bonsai-foliage foliage-1" style={{ backgroundColor: config.leafColor }} />
          <div className="bonsai-foliage foliage-2" style={{ backgroundColor: config.leafColor }} />
          <div className="bonsai-foliage foliage-3" style={{ backgroundColor: config.leafColor }} />
        </div>
      )}
    </div>
  )
}

// ============================================
// LEGENDARY PLANTS (500 bits)
// ============================================

function CherryBlossomPlant({ config, growthStage }) {
  const showBranches = growthStage >= 30
  const showBlossoms = growthStage >= 50
  const blossomCount = Math.min(8, Math.floor((growthStage - 50) / 6))
  const showPetals = growthStage >= 80

  return (
    <div className="cherry-blossom-plant legendary-plant">
      <div className="cherry-trunk" style={{ backgroundColor: config.trunkColor }} />
      {showBranches && (
        <>
          <div className="cherry-branch branch-l" style={{ backgroundColor: config.branchColor }} />
          <div className="cherry-branch branch-r" style={{ backgroundColor: config.branchColor }} />
          {growthStage >= 60 && (
            <div className="cherry-branch branch-top" style={{ backgroundColor: config.branchColor }} />
          )}
        </>
      )}
      {showBlossoms && (
        <div className="cherry-blossoms">
          {Array.from({ length: blossomCount }).map((_, i) => (
            <div
              key={i}
              className={`cherry-blossom-flower blossom-${i + 1}`}
              style={{ backgroundColor: config.petalColors[i % config.petalColors.length] }}
            />
          ))}
        </div>
      )}
      {showPetals && (
        <div className="cherry-falling-petals">
          <div className="falling-petal petal-f1" style={{ backgroundColor: config.petalColors[0] }} />
          <div className="falling-petal petal-f2" style={{ backgroundColor: config.petalColors[1] }} />
          <div className="falling-petal petal-f3" style={{ backgroundColor: config.petalColors[2] }} />
        </div>
      )}
      <div className="legendary-aura" />
    </div>
  )
}

function CrystalTreePlant({ config, growthStage }) {
  const crystalCount = Math.min(6, Math.floor(growthStage / 15))
  const showGlow = growthStage >= 60

  return (
    <div className="crystal-tree-plant legendary-plant">
      <div className="crystal-base" style={{ backgroundColor: config.baseColor }} />
      <div className="crystal-trunk" style={{ backgroundColor: config.trunkColor }}>
        <div className="crystal-facet facet-1" />
        <div className="crystal-facet facet-2" />
      </div>
      <div className="crystal-cluster">
        {Array.from({ length: crystalCount }).map((_, i) => (
          <div
            key={i}
            className={`crystal-shard shard-${i + 1}`}
            style={{
              backgroundColor: config.crystalColors[i % config.crystalColors.length],
              height: `${8 + (i % 3) * 4}px`,
              transform: `rotate(${-40 + i * 16}deg) translateY(-${4 + i * 2}px)`
            }}
          />
        ))}
      </div>
      {showGlow && <div className="crystal-inner-glow" />}
      <div className="legendary-aura" />
    </div>
  )
}

function VenusFlyTrapPlant({ config, growthStage }) {
  const showTraps = growthStage >= 40
  const trapCount = Math.min(3, Math.floor((growthStage - 40) / 20))
  const showTeeth = growthStage >= 70

  return (
    <div className="venus-flytrap-plant legendary-plant">
      <div className="venus-stem" style={{ backgroundColor: config.stemColor }} />
      {growthStage >= 20 && (
        <>
          <div className="venus-leaf leaf-l" style={{ backgroundColor: config.leafColor }} />
          <div className="venus-leaf leaf-r" style={{ backgroundColor: config.leafColor }} />
        </>
      )}
      {showTraps && (
        <div className="venus-traps">
          {Array.from({ length: trapCount }).map((_, i) => (
            <div key={i} className={`venus-trap trap-${i + 1}`}>
              <div className="trap-top" style={{ backgroundColor: config.trapColor }}>
                <div className="trap-inner" style={{ backgroundColor: config.innerColor }} />
                {showTeeth && (
                  <div className="trap-teeth">
                    {Array.from({ length: 5 }).map((_, t) => (
                      <div key={t} className="tooth" style={{ backgroundColor: config.teethColor }} />
                    ))}
                  </div>
                )}
              </div>
              <div className="trap-bottom" style={{ backgroundColor: config.trapColor }}>
                <div className="trap-inner" style={{ backgroundColor: config.innerColor }} />
                {showTeeth && (
                  <div className="trap-teeth bottom">
                    {Array.from({ length: 5 }).map((_, t) => (
                      <div key={t} className="tooth" style={{ backgroundColor: config.teethColor }} />
                    ))}
                  </div>
                )}
              </div>
              <div className="trap-stem" style={{ backgroundColor: config.stemColor }} />
            </div>
          ))}
        </div>
      )}
      <div className="legendary-aura" />
    </div>
  )
}

export default TerrariumPlant